'use client';

import { useEffect, useMemo } from 'react';

import Loading from '@/app/components/Loading';
import SportIcon from '@/app/activities/SportIcon';
import type { Week } from './InfiniteCalendar';
import { getSportIconColor, adjustColorSaturation } from '@trainme/api/client';
import { trpc } from '@/app/api/trpc/client';
import type {
  ActivityWithRelations,
  WorkoutWithRelations,
} from '@/utils/types';
import type { WeekSummaryVariant } from './CalendarWeekSummary';
import {
  buildWeeklySummaryFromActivitiesAndWorkouts,
  sortWeeklySummary,
} from './utils/weeklySummary';

const RING_SIZE = 110;
const DASHBOARD_RING_SIZE = 170;
const RING_STROKE = 6;
const RING_GAP = 3;

const getRingStrokeWidth = (center: number, layers: number) => {
  if (layers <= 0) return RING_STROKE;
  // Keep the inner hole large enough that the centered percent label (worst
  // case "100%") and sport icon never crowd or overlap the ring itself --
  // see NAO-107. A fixed 0.4 fraction left only ~44px of hole on the 110px
  // calendar ring, just about the width of "100%" at text-xl/font-semibold.
  const minInnerRadius = center * 0.58;
  const totalGap = Math.max(layers - 1, 0) * RING_GAP;
  const totalThickness = center - minInnerRadius;
  const availableThickness = Math.max(totalThickness - totalGap, 0);
  return availableThickness > 0
    ? availableThickness / layers
    : Math.max(RING_STROKE, totalThickness / layers);
};

type SportProgress = {
  sportType: string;
  doneDistance: number;
  doneTime: number;
  plannedDistance: number;
  plannedTime: number;
};

type RingDescriptor = {
  progress: number;
  color: string;
};

type PlanInfo = {
  planValue: number;
  doneValue: number;
  progress: number;
};

export type DisplayMetric = 'time' | 'distance';

type WeeklyFinishChartProps = {
  week: Week;
  selectedSportId?: string | null;
  displayMetric: DisplayMetric;
  onSelectSport?: (sportType: string) => void;
  activities?: ActivityWithRelations[];
  workouts?: WorkoutWithRelations[];
  variant?: WeekSummaryVariant;
};

export default function WeeklyFinishChart({
  week,
  selectedSportId,
  displayMetric,
  onSelectSport,
  activities,
  workouts,
  variant = 'calendar',
}: WeeklyFinishChartProps) {
  const weeklySummaryQuery = trpc.activities.getWeeklySummary.useQuery({
    aday: week.start,
    filter: {
      date: {
        gte: week.start,
        lt: week.end,
      },
    },
  });

  const weeklySummaryData = weeklySummaryQuery.data ?? [];
  const fallbackSummary = useMemo(
    () => buildWeeklySummaryFromActivitiesAndWorkouts(activities, workouts),
    [activities, workouts]
  );
  const summaryRows =
    weeklySummaryData.length > 0 ? weeklySummaryData : fallbackSummary;
  const sortedSummaryRows = useMemo(
    () => [...summaryRows].sort(sortWeeklySummary),
    [summaryRows]
  );

  const sportProgress = useMemo(() => {
    return sortedSummaryRows.map((entry) => ({
      sportType: entry.sportType || '',
      doneDistance: entry.doneDistance ?? 0,
      doneTime: entry.doneTime ?? 0,
      plannedDistance: entry.plannedDistance ?? 0,
      plannedTime: entry.plannedTime ?? 0,
    }));
  }, [sortedSummaryRows]);

  const topSports = useMemo(() => sportProgress, [sportProgress]);

  const resolvedSelectedSportId =
    selectedSportId ?? topSports[0]?.sportType ?? null;

  useEffect(() => {
    if (!selectedSportId && topSports.length > 0) {
      const defaultSport = topSports[0].sportType;
      onSelectSport?.(defaultSport);
    }
  }, [topSports, onSelectSport, selectedSportId]);

  const selectedSport = useMemo(() => {
    if (topSports.length === 0) return null;
    return (
      topSports.find((s) => s.sportType === resolvedSelectedSportId) ||
      topSports[0]
    );
  }, [topSports, resolvedSelectedSportId]);

  const ringDescriptors = useMemo(() => {
    return topSports.map((entry) => {
      const info = getPlanInfo(entry, displayMetric);
      const baseColor = getSportIconColor(entry.sportType);
      const isActive = entry.sportType === selectedSport?.sportType;
      return {
        progress: info.progress,
        color: isActive
          ? baseColor
          : adjustColorSaturation(baseColor, 0.35, 0.65),
      } as RingDescriptor;
    });
  }, [selectedSport, topSports, displayMetric]);

  const selectedPlanInfo = selectedSport
    ? getPlanInfo(selectedSport, displayMetric)
    : null;
  const selectedPercentText = selectedPlanInfo
    ? selectedPlanInfo.planValue > 0
      ? `${Math.round(selectedPlanInfo.progress * 100)}%`
      : selectedPlanInfo.doneValue > 0
        ? '100%'
        : '—'
    : '—';

  const isLoading = weeklySummaryQuery.isLoading;

  return (
    <div className='p-2'>
      <div className='mt-1 flex flex-col items-center gap-1'>
        {isLoading ? (
          <Loading
            size={16}
            className='text-emerald-500 dark:text-emerald-400'
          />
        ) : (
          ringDescriptors.length > 0 && (
            <>
              <div className='relative'>
                <WeeklyRingsSvg
                  size={
                    variant === 'dashboard' ? DASHBOARD_RING_SIZE : RING_SIZE
                  }
                  rings={ringDescriptors}
                />
                <div className='absolute inset-0 flex flex-col items-center justify-center pointer-events-none'>
                  <span className='text-xl font-semibold text-gray-900 dark:text-white'>
                    {selectedPercentText}
                  </span>
                  {selectedSport && (
                    <SportIcon type={selectedSport.sportType} size={20} />
                  )}
                </div>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

const WeeklyRingsSvg = ({
  size,
  rings,
}: {
  size: number;
  rings: RingDescriptor[];
}) => {
  if (rings.length === 0) return null;
  const center = size / 2;
  const strokeWidth = getRingStrokeWidth(center, rings.length);

  const layouts = rings.map((ring, index) => {
    const offset = index * (strokeWidth + RING_GAP);
    const radius = center - offset - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;
    return {
      ...ring,
      radius,
      circumference,
      strokeWidth,
    };
  });

  if (layouts.every((ring) => ring.radius <= 0)) {
    return null;
  }

  return (
    <svg width={size} height={size} role='presentation' className='block'>
      {layouts.map((ring, index) => {
        if (ring.radius <= 0) return null;
        const progress = Math.min(Math.max(ring.progress, 0), 1);
        const dashOffset = ring.circumference * (1 - progress);
        return (
          <g
            key={`${ring.color}-${index}`}
            transform={`rotate(-90 ${center} ${center})`}
          >
            <circle
              cx={center}
              cy={center}
              r={ring.radius}
              stroke='rgba(148, 163, 184, 0.3)'
              strokeWidth={ring.strokeWidth}
              fill='none'
            />
            <circle
              cx={center}
              cy={center}
              r={ring.radius}
              stroke={ring.color}
              strokeWidth={ring.strokeWidth}
              strokeDasharray={ring.circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap='round'
              fill='none'
            />
          </g>
        );
      })}
    </svg>
  );
};

const getPlanInfo = (entry: SportProgress, metric: DisplayMetric): PlanInfo => {
  const distancePlanMeters = entry.plannedDistance
    ? entry.plannedDistance * 1000
    : 0;
  const planValue =
    metric === 'time'
      ? entry.plannedTime
        ? entry.plannedTime * 60
        : 0
      : distancePlanMeters;
  const doneValue = metric === 'time' ? entry.doneTime : entry.doneDistance;
  let progress = 0;
  if (planValue > 0) {
    progress = Math.min(doneValue / planValue, 1);
  } else if (doneValue > 0) {
    progress = 1;
  }
  const displayPlanValue = planValue > 0 ? planValue : doneValue;
  return { planValue: displayPlanValue, doneValue, progress };
};
